// src/components/ImprovedSidebar.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { usePerformansBolumKontrol } from '../hooks/usePerformansBolumKontrol';
import { useAppContext } from '../contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { 
  Home, ListChecks, ClipboardList, FileInput, 
  CheckSquare, BarChart2, FileText, User, 
  LogOut, ShieldCheck, Menu, X, AlertTriangle,
  Target, Shield, ChevronDown, ChevronRight,
  Database, Settings, TrendingUp, BookOpen, MessageCircle
} from 'lucide-react';
import ekostratejiLogo from '../assets/ekostrateji-logo.png';

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

interface MenuGroup {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  items: MenuItem[];
  permission?: string;
}

interface MenuItem {
  path: string;
  title: string;
  icon: React.ComponentType<any>;
  permission?: string;
}

const ImprovedSidebar: React.FC<SidebarProps> = ({ 
  isCollapsed = false, 
  onToggle, 
  isMobileOpen = false, 
  onMobileClose,
  isMobile = false 
}) => {
  const { t } = useTranslation('common');
  const { profile, signOut, loading } = useAuth();
  const { hasPermission } = usePermissions();

  const { selectedFirma, selectedYil } = useAppContext();
  const { hasAnyAssignment } = usePerformansBolumKontrol(
    profile?.bolum_id,
    selectedFirma?.firma_adi || selectedFirma?.value || null,
    selectedYil
  );
  
  // Performance control logging removed for cleaner console
  const navigate = useNavigate();
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(isCollapsed);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { i18n } = useTranslation();
  
  // Menu configuration
  const menuGroups: MenuGroup[] = useMemo(() => [
    {
      id: 'veri',
      title: t('sidebar.dataManagement'),
      icon: Database,
      items: [
        { path: '/konu-atama', title: t('nav.topicAssignment'), icon: ClipboardList, permission: 'topic_assignment_view' },
        { path: '/veri-toplama-sureci', title: t('nav.dataCollection'), icon: FileInput, permission: 'data_collection_view' },
        { path: '/veri-toplama-sureci/index', title: 'SASB Index', icon: ListChecks, permission: 'sasb_index_view' }
      ]
    },
    {
      id: 'anket',
      title: t('sidebar.analysis'),
      icon: CheckSquare,
      items: [
        { path: '/anket-hazirla', title: t('nav.surveyPrepare'), icon: CheckSquare, permission: 'survey_create' },
        { path: '/cifte-onemlilik-matrisi', title: t('nav.materiality'), icon: BarChart2, permission: 'materiality_matrix_view' }
      ]
    },
    {
      id: 'tsrs',
      title: t('nav.tsrs'),
      icon: FileText,
      items: [
        { path: '/tsrs', title: t('nav.tsrsGeneral'), icon: FileText },
        { path: '/beceri-yetkinlik-matrisi', title: t('nav.skillMatrix'), icon: Database },
        { path: '/deger-zinciri', title: t('nav.valueChain'), icon: Target },
        { path: '/riskler', title: t('nav.risks'), icon: AlertTriangle },
        { path: '/firsatlar', title: t('nav.opportunities'), icon: TrendingUp },
        { path: '/uyum-stratejileri', title: t('nav.compliance'), icon: Shield },
        { path: '/tsrs-sera-gazi-emisyonlari', title: 'Sera Gazı Emisyonları (SAP)', icon: Database },
        { path: '/tsrs-sera-gazi-hedef-tablosu', title: t('nav.ghgTargetTable'), icon: Target },
        { path: '/hedefler', title: t('nav.targets'), icon: Target }
      ]
    },
    {
      id: 'gri',
      title: t('nav.gri'),
      icon: BookOpen,
      items: [
        { path: '/gri-genel', title: t('nav.griGeneral'), icon: FileText },
        { path: '/gri-evrensel', title: t('nav.griUniversal'), icon: Settings },
        { path: '/gri-ekonomik', title: t('nav.griEconomic'), icon: TrendingUp },
        { path: '/gri-cevresel', title: t('nav.griEnvironmental'), icon: Shield },
        { path: '/gri-sosyal', title: t('nav.griSocial'), icon: User }
      ]
    }
  ], [t, i18n.language]);

  // Auto-expand based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpanded = new Set<string>();
    
    menuGroups.forEach(group => {
      const hasActiveItem = group.items.some(item => currentPath === item.path);
      if (hasActiveItem) {
        newExpanded.add(group.id);
      }
    });
    
    setExpandedGroups(newExpanded);
  }, [location.pathname, menuGroups]);

  useEffect(() => {
    setInternalCollapsed(isCollapsed);
  }, [isCollapsed]);

  const handleToggle = () => {
    const newCollapsed = !internalCollapsed;
    setInternalCollapsed(newCollapsed);
    if (onToggle) {
      onToggle();
    }
  };

  const toggleGroup = (groupId: string) => {
    const effectiveCollapsed = isMobile ? false : internalCollapsed;
    if (effectiveCollapsed) return;
    
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const hasPermissionAccess = (permission?: string) => {
    if (!permission) return true;
    return hasPermission(permission);
  };

  const groupHasAnyAccess = (group: MenuGroup) => {
    if (group.permission && !hasPermissionAccess(group.permission)) {
      return false;
    }
    return group.items.some(item => hasPermissionAccess(item.permission));
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const handleLinkClick = () => {
    // Mobilde link'e tıklandığında menüyü kapat
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const currentCollapsed = isMobile ? false : internalCollapsed;

  return (
    <>
      <aside className={`sidebar-container improved ${currentCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          {/* Desktop toggle button - Sol tarafta */}
          {!isMobile && (
            <button 
              className="sidebar-toggle-btn desktop-only"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggle();
              }}
            >
              {currentCollapsed ? <Menu size={20} /> : <X size={20} />}
            </button>
          )}
          
          {/* Logo - Yan tarafta */}
          {!currentCollapsed && (!isMobile || isMobileOpen) && (
            <img src={ekostratejiLogo} alt="Ekostrateji Logo" className="sidebar-logo" />
          )}
        </div>

        <nav className="sidebar-nav improved">
          {loading && (
            <div className="sidebar-loading">
              <div className="loading-skeleton">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton-item" />
                ))}
              </div>
            </div>
          )}
          
          {!loading && (
            <>
              {/* Anasayfa - Top level item */}
              {hasPermission('dashboard_view') && (
                <NavLink 
                  to="/anasayfa" 
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  title={currentCollapsed ? t('nav.dashboard') : ""}
                  onClick={handleLinkClick}
                >
                  <Home className="sidebar-link-icon" size={22} />
                  {!currentCollapsed && <span className="sidebar-link-text">{t('nav.dashboard')}</span>}
                </NavLink>
              )}

              {/* Önemli Konu Seçimi - Top level item - Bölüm Kullanıcısı için gizle */}
              {hasPermission('tsrs_entities_view') && profile?.rol !== 'bolum_kullanicisi' && (
                <NavLink 
                  to="/kapsanan-isletmeler" 
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  title={currentCollapsed ? t('nav.facilities') : ""}
                >
                  <Database className="sidebar-link-icon" size={22} />
                  {!currentCollapsed && <span className="sidebar-link-text">{t('nav.facilities')}</span>}
                </NavLink>
              )}

              {hasPermission('topic_selection_view') && profile?.rol !== 'bolum_kullanicisi' && (
                <NavLink 
                  to="/onemli-konu-secimi" 
                  className={({ isActive }) => `sidebar-link topic-selection-link ${isActive ? 'active' : ''}`}
                  title={currentCollapsed ? t('nav.topicSelection') : ""}
                  onClick={handleLinkClick}
                >
                  <ListChecks className="sidebar-link-icon" size={22} />
                  {!currentCollapsed && <span className="sidebar-link-text">{t('nav.topicSelection')}</span>}
                </NavLink>
              )}

              {menuGroups.map(group => {
                // Bölüm Kullanıcısı için sadece Veri Yönetimi grubunu göster
                if (profile?.rol === 'bolum_kullanicisi' && group.id !== 'veri') {
                  return null;
                }
                if (!groupHasAnyAccess(group)) return null;
                
                const isExpanded = expandedGroups.has(group.id);
                const hasActiveItem = group.items.some(item => 
                  location.pathname === item.path && hasPermissionAccess(item.permission)
                );

                return (
                  <div key={group.id} className={`menu-group ${isExpanded ? 'expanded' : ''}`}>
                    <div 
                      className={`group-header ${hasActiveItem ? 'has-active' : ''}`}
                      onClick={() => toggleGroup(group.id)}
                      title={currentCollapsed ? group.title : ''}
                    >
                      <group.icon className="group-icon" size={22} />
                      {!currentCollapsed && (
                        <>
                          <span className="group-title">{group.title}</span>
                          <div className="group-chevron">
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {isExpanded && !currentCollapsed && (
                      <div className="group-items">
                        {group.items.map(item => {
                          if (!hasPermissionAccess(item.permission)) return null;
                          
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              end
                              className={({ isActive }) => `group-item ${isActive ? 'active' : ''}`}
                              title={item.title}
                              onClick={handleLinkClick}
                            >
                              <item.icon className="item-icon" size={18} />
                              <span className="item-title">{item.title}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Performans İşlemleri - Group menu veya tek item */}
              {(() => {
                const hasPerf = hasPermission('performance_indicators_view');
                const isBolumUser = profile?.rol === 'bolum_kullanicisi';
                const shouldShow = hasPerf && (!isBolumUser || hasAnyAssignment);
                
                if (!shouldShow) return null;
                
                // Bölüm kullanıcısı için sadece Performans Göstergeleri göster (grup değil, tek item)
                if (isBolumUser) {
                  return (
                    <NavLink 
                      key="performans-gostergeleri"
                      to="/performans-gostergeleri" 
                      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                      title={currentCollapsed ? t('nav.performanceIndicators') : ""}
                      onClick={handleLinkClick}
                    >
                      <BarChart2 className="sidebar-link-icon" size={22} />
                      {!currentCollapsed && <span className="sidebar-link-text">{t('nav.performanceIndicators')}</span>}
                    </NavLink>
                  );
                }
                
                // Admin'ler için grup menü
                const performansGroup: MenuGroup = {
                  id: 'performans',
                  title: t('nav.performanceOperations'),
                  icon: BarChart2,
                  items: [
                    { path: '/performans-bolum-ata', title: t('nav.performanceDepartmentAssign'), icon: ClipboardList },
                    { path: '/performans-gostergeleri', title: t('nav.performanceIndicators'), icon: BarChart2 }
                  ]
                };
                const isExpanded = expandedGroups.has(performansGroup.id);
                const hasActiveItem = performansGroup.items.some(item => location.pathname === item.path);

                return (
                  <div key={performansGroup.id} className={`menu-group ${isExpanded ? 'expanded' : ''}`}>
                    <div 
                      className={`group-header ${hasActiveItem ? 'has-active' : ''}`}
                      onClick={() => toggleGroup(performansGroup.id)}
                      title={currentCollapsed ? performansGroup.title : ''}
                    >
                      <performansGroup.icon className="group-icon" size={22} />
                      {!currentCollapsed && (
                        <>
                          <span className="group-title">{performansGroup.title}</span>
                          <div className="group-chevron">
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {isExpanded && !currentCollapsed && (
                      <div className="group-items">
                        {performansGroup.items.map(item => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end
                            className={({ isActive }) => `group-item ${isActive ? 'active' : ''}`}
                            title={item.title}
                            onClick={handleLinkClick}
                          >
                            <item.icon className="item-icon" size={20} />
                            <span className="item-title">{item.title}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Senaryo Analizi - Top level item - Sadece Süper Admin */}
              {profile?.rol === 'ekostrateji_super_admin' && (
                <NavLink 
                  to="/senaryo-analizi-v3" 
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  title={currentCollapsed ? t('nav.scenarioAnalysis') : ""}
                  onClick={handleLinkClick}
                >
                  <TrendingUp className="sidebar-link-icon" size={22} />
                  {!currentCollapsed && <span className="sidebar-link-text">{t('nav.scenarioAnalysis')}</span>}
                </NavLink>
              )}

              {/* Raporlama - Separate item - Bölüm Kullanıcısı için gizle */}
              {hasPermission('reports_view') && profile?.rol !== 'bolum_kullanicisi' && (
                <div className="menu-separator">
                  <NavLink 
                    to="/raporlama" 
                    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                    title={currentCollapsed ? t('nav.reporting') : ""}
                    onClick={handleLinkClick}
                  >
                    <FileText className="sidebar-link-icon" size={22} />
                    {!currentCollapsed && <span className="sidebar-link-text">{t('nav.reporting')}</span>}
                  </NavLink>
                </div>
              )}

              {/* Bilgi Bankası - Separate item - Bölüm Kullanıcısı için gizle */}
              {hasPermission('knowledge_base_view') && profile?.rol !== 'bolum_kullanicisi' && (
                <div className="menu-separator">
                  <NavLink 
                    to="/bilgi-bankasi" 
                    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                    title={currentCollapsed ? t('nav.knowledgeBase') : ""}
                    onClick={handleLinkClick}
                  >
                    <BookOpen className="sidebar-link-icon" size={22} />
                    {!currentCollapsed && <span className="sidebar-link-text">{t('nav.knowledgeBase')}</span>}
                  </NavLink>
                </div>
              )}

              {/* AI Chat - Separate item - Sadece Süper Admin */}
              {profile?.rol === 'ekostrateji_super_admin' && (
                <div className="menu-separator">
                  <NavLink 
                    to="/ai-chat" 
                    className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                    title={currentCollapsed ? t('nav.aiAssistant') : ""}
                    onClick={handleLinkClick}
                  >
                    <MessageCircle className="sidebar-link-icon" size={22} />
                    {!currentCollapsed && <span className="sidebar-link-text">{t('nav.aiAssistant')}</span>}
                  </NavLink>
                </div>
              )}

              {/* Admin Panel - Separate item */}
              {hasPermission('admin_panel_access') && (
                <div className="menu-separator">
                  <NavLink 
                    to="/admin/dashboard" 
                    className={({ isActive }) => `sidebar-link admin-link ${isActive ? 'active' : ''}`}
                    title={currentCollapsed ? t('nav.adminPanel') : ""}
                    onClick={handleLinkClick}
                  >
                    <ShieldCheck className="sidebar-link-icon" size={22} />
                    {!currentCollapsed && <span className="sidebar-link-text">{t('nav.adminPanel')}</span>}
                  </NavLink>
                </div>
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          {hasPermission('profile_view') && (
            <NavLink 
              to="/profilim" 
              className="sidebar-link footer-link"
              title={currentCollapsed ? t('nav.profile') : ""}
              onClick={handleLinkClick}
            >
              <User className="sidebar-link-icon" size={22} />
              {!currentCollapsed && <span className="sidebar-link-text">{t('nav.profile')}</span>}
            </NavLink>
          )}
          <button 
            onClick={handleLogout} 
            className="sidebar-link footer-link logout-btn"
            title={currentCollapsed ? t('auth.logout') : ""}
          >
            <LogOut className="sidebar-link-icon" size={22} />
            {!currentCollapsed && <span className="sidebar-link-text">{t('auth.logout')}</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default ImprovedSidebar;
